import * as _ from 'lodash';
import {
  all,
  call,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import Cookies from 'universal-cookie';

import {
  LOAD_USER,
  LOAD_USER_ERROR,
  LOAD_USER_SUCCESS,
  LOGIN,
  LOGIN_ERROR,
  LOGIN_SUCCESS,
  LOGOUT,
  PATCH_USER,
  PATCH_USER_ERROR,
  PATCH_USER_SUCCESS,
  SET_USER_TOKEN,
  SIGNUP,
  SIGNUP_ERROR,
  SIGNUP_SUCCESS,
  setUserToken,
} from '../actions/account';

import { getToken, getUser } from '../selectors/account';
import { getTokenName } from '../selectors/env';
import api from './api';
import { minDelayCall } from './helpers';

export function * doSignup (action) {
  const { formData } = action;

  try {
    const response = yield minDelayCall(api.signup, formData);

    // If the response had an error status, but wasn't caught as an error
    if (!_.isNil(response.status) && response.status > 299) {
      yield put({
        type: SIGNUP_ERROR, 
        error: _.get(response, 'data'),
      });
    } else {
      response['nextRoute'] = formData.nextRoute;

      // Persist token for future page loads
      const tokenName = yield select(getTokenName);
      new Cookies().set(tokenName, response.key, { path: '/' });

      yield put({ type: SIGNUP_SUCCESS, ...response });
    }
  } catch (error) {
    yield put({ type: SIGNUP_ERROR, error: _.get(error, 'response.data', error) });
  }
}

export function* doLogin (action) {
  const { formData } = action;

  try {
    const response = yield minDelayCall(api.login, formData);

    // If the response had an error status, but wasn't caught as an error
    if (!_.isNil(response.status) && response.status > 299) {
      yield put({
        type: LOGIN_ERROR, 
        error: _.get(response, 'data'),
      });
    } else {
      response['nextRoute'] = formData.nextRoute;

      // Persist token for future page loads
      const tokenName = yield select(getTokenName);
      new Cookies().set(tokenName, response.key, { path: '/' });

      yield put({ type: LOGIN_SUCCESS, ...response });
    }
  } catch (error) {
    yield put({ type: LOGIN_ERROR, error: _.get(error, 'response.data', error) });
  }
}

export function* loadUser ( token = null ) {
  // Set user loading
  yield put({ type: LOAD_USER });

  try {

    if(token) {
      yield put(setUserToken(token));
    }

    if (yield select(getToken)) {
      const user = yield minDelayCall(api.loadUser);

      yield put({ type: LOAD_USER_SUCCESS, user});
    } else {
      yield put({ type: LOAD_USER_ERROR });
    }

  } catch (error) {
    // Token is bogus. Remove it.
    const tokenName = yield select(getTokenName);
    new Cookies().remove(tokenName, { path: '/' });

    yield put({ type: LOAD_USER_ERROR });
  }
}

export function* doPatchUser (action) {
  const { formData, headers, toast } = action;
  const response = yield call(api.patchUser, formData, headers);

  // If the response had an error status, but wasn't caught as an error
  if (!_.isNil(response.status) && response.status > 299) {
    yield put({
      type: PATCH_USER_ERROR,
      error: _.get(response, 'data'),
    });
  } else {
    yield put({ type: PATCH_USER_SUCCESS, response });
    if(toast) {
      toast.success('User value has been updated', {
        bodyClassName: 'toast-body',
        style: {
          backgroundColor: '#414B4F',
          color: '#ffffff',
        },
        progressStyle: {
          background: '#00c9b7',
        },
        position: 'bottom-right',
      });
    }
  }
}

// Finite state machine for user session
export function* loginFlow () {
  while(true) {
    // Check for existing token
    const tokenName = yield select(getTokenName);
    const token = new Cookies().get(tokenName);

    if (token) {
      // Load the user if we found a token
      yield call(loadUser, token);
    }

    if (!(yield select(getUser))) {
      // Wait for login / signup if we don't have a user
      const action = yield take([LOGIN, SIGNUP, SET_USER_TOKEN]);

      switch (action.type) {
        case LOGIN:
          yield call(doLogin, action);
          break;
        case SIGNUP:
          yield call(doSignup, action);
          break;
        case SET_USER_TOKEN:
          yield call(loadUser);
      }
    }

    if (yield select(getUser)) {
      // Wait for logout
      yield take(LOGOUT);

      const tokenName = yield select(getTokenName);
      new Cookies().remove(tokenName, { path: '/' });
    }
  }
}

function* doLogOut (params) {
  const paramsType = _.get(params, 'params.type', false);
  if(paramsType === 'token-invalidation') {
    yield cancel(loginFlow);
  }
}

export default function* () {
  yield all([
    loginFlow(),
    takeEvery(PATCH_USER, doPatchUser),
    takeLatest(LOGOUT, doLogOut),
  ]);
}
